"use client";

;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { isCancelledError } from "@tanstack/react-query";
import { useRouter, useSelectedLayoutSegment } from "next/navigation";
import * as React from "react";
import { useShallow } from "zustand/react/shallow";
import { usePageActivityTracking } from "@/hooks/use-page-activity-tracking";
import { useSentryTenantContext } from "@/hooks/use-sentry-tenant-context";
import { decodeUserJwtToken } from "@/lib/auth/jwt";
import { ROUTES } from "@/lib/constants/routes";
import { emitter } from "@/lib/emitter";
import { logger } from "@/lib/services/logger";
import { useAuthStore } from "@/lib/stores/authStore";
import { getWorkspacesDefaultViewPath } from "@/lib/utils";
import { chatbase } from "@/lib/utils/chatbase";
import { SessionActivity, sessionActivityHandler } from "@/lib/utils/session-activity";
import { smartlook } from "@/lib/utils/smartlook";
import { getQueryClient } from "@/queries/config/query-client";
import { getUserQueryOptions } from "@/queries/user/fetchers";
import type { UserDataResponse } from "@/types/user";
interface AuthContextType {
  loading: boolean;
  signIn: (data: {
    token: string;
    chatbaseIdentityToken?: string;
  }, redirect?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  reauthenticate: () => Promise<void>;
  redirectIfAuthenticated: (path: string) => void;
}
const AuthContext = React.createContext<AuthContextType>({
  loading: true,
  reauthenticate: async () => {},
  redirectIfAuthenticated: () => {},
  signIn: async () => {},
  signOut: async () => {}
});
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const segment = useSelectedLayoutSegment();
  const [loading, startTransition] = React.useTransition();
  const router = useRouter();
  const queryClient = getQueryClient();
  const {
    _hasHydrated: isHydrated,
    setUser: setAuthStoreUser,
    setAuthenticated,
    isAuthenticated,
    user,
    logout
  } = useAuthStore(useShallow(state => state));
  // Track page navigation for session activity
  usePageActivityTracking();
  useSentryTenantContext();

  /**
   * `router.push` inside a transition only commits once the destination RSC
   * payload arrives, and each new push restarts that wait. Logging out makes
   * every in-flight query 401, and each one emits `user.unauthorized`, so
   * without this guard a single sign-out queues a dozen competing redirects
   * that can starve each other indefinitely on a slow connection.
   */
  const isRedirectingToLoginRef = React.useRef(false);
  const isSigningInRef = React.useRef(false);
  const isOnProtectedSegment = Boolean(segment?.includes("protected"));
  const redirectIfAuthenticated = React.useCallback((path: string) => {
    const isAuthenticated_0 = useAuthStore.getState().isAuthenticated;
    if (isAuthenticated_0) {
      const params = new URLSearchParams(window.location.search);
      const redirectPath = params.get("redirect") || path;
      router.replace(redirectPath);
    }
  }, [router]);
  const signIn = React.useCallback(async (data: {
    token: string;
    chatbaseIdentityToken?: string;
  }, redirect = true) => {
    isSigningInRef.current = true;
    try {
      const {
        token,
        chatbaseIdentityToken
      } = data;
      const decodedToken = decodeUserJwtToken(token);

      // A new session supersedes any redirect the previous one left behind.
      isRedirectingToLoginRef.current = false;

      // Update Zustand store
      setAuthStoreUser({
        email: decodedToken.email,
        id: decodedToken.user_id,
        organizationId: decodedToken.organizationId,
        role: decodedToken.role
      });
      smartlook.identify(decodedToken.user_id, {
        email: decodedToken.email,
        organizationId: decodedToken.organizationId,
        role: decodedToken.role
      });
      if (chatbaseIdentityToken) {
        chatbase.identify({
          email: decodedToken.email,
          token: chatbaseIdentityToken
        });
      }
      if (!redirect) {
        // Record sign up session activity
        sessionActivityHandler.recordActivity({
          email: decodedToken.email,
          pageActivity: [{
            action: SessionActivity.pageActivityActions.SIGN_UP,
            payload: {
              email: decodedToken.email,
              organizationId: decodedToken.organizationId,
              role: decodedToken.role
            },
            time: new Date().toISOString()
          }],
          pagePath: window.location.pathname
        });
      }
      if (redirect) {
        setAuthenticated(true);
        logger.info("User signed in successfully", {
          email: decodedToken.email,
          userId: decodedToken.user_id
        });
        const userQueryOptions = getUserQueryOptions(decodedToken.user_id);
        let userData: UserDataResponse;
        try {
          userData = await queryClient.ensureQueryData(userQueryOptions);
        } catch (error_0) {
          if (!isCancelledError(error_0)) {
            throw error_0;
          }
          userData = await queryClient.ensureQueryData(userQueryOptions);
        }
        const nextUrl = getWorkspacesDefaultViewPath(userData?.workspaces || []);

        // Record sign in session activity
        sessionActivityHandler.recordActivity({
          email: decodedToken.email,
          pageActivity: [{
            action: SessionActivity.pageActivityActions.SIGN_IN,
            payload: {
              email: decodedToken.email,
              organizationId: decodedToken.organizationId,
              role: decodedToken.role
            },
            time: new Date().toISOString()
          }, {
            action: SessionActivity.pageActivityActions.PAGE_NAVIGATION,
            payload: {
              currentPagePath: window.location.pathname,
              nextPagePath: nextUrl
            },
            time: new Date().toISOString()
          }],
          pagePath: window.location.pathname,
          workspaceId: nextUrl.split("/")[0]
        });
        redirectIfAuthenticated(nextUrl);
      }
    } catch (error) {
      logger.error("Error signing in with custom token", error instanceof Error ? error : undefined);
      throw error;
    } finally {
      isSigningInRef.current = false;
    }
  }, [setAuthStoreUser, setAuthenticated, queryClient, redirectIfAuthenticated]);
  const signOut = React.useCallback(async (redirect_0?: string) => {
    if (isRedirectingToLoginRef.current) {
      return;
    }
    isRedirectingToLoginRef.current = true;
    startTransition(async () => {
      try {
        let LOGIN_URL: string = ROUTES.HOME;

        // Let's setup a redirect back
        if (redirect_0) {
          LOGIN_URL = `${LOGIN_URL}?redirect=${redirect_0}`;
        }
        sessionActivityHandler.endSession();
        await logout();
        smartlook.anonymize();
        chatbase.resetUser();
        logger.info("User signed out successfully");
        router.push(LOGIN_URL);
      } catch (error_1) {
        isRedirectingToLoginRef.current = false;
        logger.error("Error signing out", error_1 instanceof Error ? error_1 : undefined);
        throw error_1;
      }
    });
  }, [router, logout]);
  const reauthenticate = React.useCallback(async () => {
    if (isRedirectingToLoginRef.current) {
      return;
    }
    isRedirectingToLoginRef.current = true;
    startTransition(async () => {
      try {
        const LOGIN_URL_0: string = ROUTES.HOME;
        await logout(true);
        smartlook.anonymize();
        chatbase.resetUser();
        logger.info("User asked to reauthenticate successfully");
        router.push(LOGIN_URL_0);
      } catch (error_2) {
        isRedirectingToLoginRef.current = false;
        logger.error("Error signing out", error_2 instanceof Error ? error_2 : undefined);
        throw error_2;
      }
    });
  }, [logout, router]);
  React.useEffect(() => {
    function handleUnauthorized() {
      if (isSigningInRef.current || !isOnProtectedSegment) {
        return;
      }
      reauthenticate();
    }
    emitter.on("user.unauthorized", handleUnauthorized);
    return () => {
      emitter.off("user.unauthorized", handleUnauthorized);
    };
  }, [isOnProtectedSegment, reauthenticate]);
  React.useEffect(function releaseLoginRedirectGuardOnPublicRoute() {
    if (!isOnProtectedSegment) {
      isRedirectingToLoginRef.current = false;
    }
  }, [isOnProtectedSegment]);
  React.useEffect(() => {
    const shouldSignOutAutomatically = (!isAuthenticated || !user) && isOnProtectedSegment && isHydrated;
    if (shouldSignOutAutomatically) {
      signOut();
    }
  }, [isAuthenticated, isHydrated, user, isOnProtectedSegment, signOut]);
  return <AuthContext.Provider value={{
    loading,
    reauthenticate,
    redirectIfAuthenticated,
    signIn,
    signOut
  }}>
			{children}
		</AuthContext.Provider>;
}
export const useAuth = () => {
  return React.useContext(AuthContext);
};